import { useEffect } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { AutocompleteDropdown, Option } from '@/schemas/sceneDataSchema';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import { encodeFieldKey } from '@/schemas/validation/ValidationSchemaGenerator';
import useAnalytics, { eventName } from '@/hooks/use-analytics';
import { useFieldBinding, seedFormFromStore } from '@/hooks/use-field-binding';

const filterOptions = createFilterOptions<Option>({
  matchFrom: 'any',
  stringify: (option) => option.label,
});

// Replaces default MUI sorting to order startsWith before contains
// ex. if you type "ka" then "Kansas" will appear before "Alaska"
const customFilterOptions = (options: Option[], params: Parameters<typeof filterOptions>[1]) => {
  const filtered = filterOptions(options, params);

  if (!params.inputValue) return filtered;

  const inputValue = params.inputValue.toLowerCase();

  return filtered.sort((a, b) => {
    const aStartsWith = a.label.toLowerCase().startsWith(inputValue);
    const bStartsWith = b.label.toLowerCase().startsWith(inputValue);

    if (aStartsWith && !bStartsWith) return -1;
    if (!aStartsWith && bStartsWith) return 1;
    return 0;
  });
};

export const AutocompleteDropdownFormElement = ({
  form,
  component,
}: {
  form: UseFormReturn;
  component: AutocompleteDropdown;
}) => {
  const { trackEvent } = useAnalytics();
  const key = encodeFieldKey(component);
  const binding = useFieldBinding(component);

  useEffect(() => {
    seedFormFromStore(form, key, binding.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Controller
      control={form.control}
      name={key}
      render={({ field, fieldState }) => {
        return (
          <FormControl
            fullWidth
            error={!!fieldState.error}
          >
            <Autocomplete<Option>
              options={component.options}
              getOptionLabel={(option) => option?.label || ''}
              isOptionEqualToValue={(option, value) => option?.value === value?.value}
              value={component.options.find((option) => option.value.toString() === field.value) ?? null}
              onChange={(_, newValue) => {
                const label = `${eventName(component.label)}`;
                if (newValue?.value) {
                  trackEvent(`${label}_${eventName(newValue.value.toString())}_SELECTED`);
                  field.onChange(newValue.value);
                  binding.sync(newValue.value.toString());
                } else {
                  trackEvent(`${label}_CLEARED`);
                  field.onChange('');
                  binding.sync('');
                }
                binding.onBlur();
              }}
              onBlur={() => {
                field.onBlur();
                binding.onBlur();
              }}
              filterOptions={customFilterOptions}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={component.label}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
              aria-label={component.label}
              clearOnEscape
              autoHighlight
            />
          </FormControl>
        );
      }}
    />
  );
};
